/**
 * Iter 34 — Admin Shared Calendar (read-only).
 *
 * The school-wide "we calendar". Reuses /schedule/team and
 * /schedule/conflicts (already cross-provider) — admin reads, doesn't
 * edit. Provider sessions, unavailability, and conflicts are all surfaced.
 *
 * Filter dimensions:
 *   - Campus
 *   - Service area (role) — derived from providers' role field
 *   - Single-provider focus (drill-in from Providers/Coverage page)
 *
 * View modes:
 *   - Day: one column per provider (default — best cross-provider read)
 *   - Week: grid per provider (collapsible)
 *
 * FP3-120 —
 *   1. Session parity with Provider Calendar. Applies the same
 *      `groupSessionsByTimeSlot` normalization that ScheduleView
 *      uses so multi-student groups (recurring blocks with 2+
 *      students AND back-to-back one-off rows on the same slot)
 *      render as ONE occurrence with all live students listed,
 *      not one pill per row on top of another. This closes the
 *      "shared calendar dropped Chloe from Chloe/Carlos" bug.
 *   2. Steady-state cache. Week/day navigation now hits the React
 *      Query cache immediately when revisiting a previously loaded
 *      week (2-min stale window). Provider unavailability is a
 *      separate cache key. Cache key embeds authScope + as_org +
 *      as_school so no cross-org leak.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight, Funnel, Eye, Warning } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { authScopeKey, STALE } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { useScheduleTeamWeek, useAdminProviders } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import { groupSessionsByTimeSlot } from "@/pages/schedule/utils";
import AdminShell from "./AdminShell";

const HOUR_PX = 64; // px per hour

function getMonday(d) {
  const m = new Date(d);
  const dow = m.getDay(); // 0=Sun
  const diff = dow === 0 ? -6 : 1 - dow;
  m.setDate(m.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return m;
}

function isoOfDay(d, offset = 0) {
  const x = new Date(d);
  x.setDate(x.getDate() + offset);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function fmtHour(h) {
  const ampm = h >= 12 ? "PM" : "AM";
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}${ampm}`;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const ROLE_COLORS = {
  SLP: "#1E5A3F",
  OT: "#9C6B2E",
  BCBA: "#3F4756",
  "Special Education Teacher": "#7C3F58",
  "School Psychologist": "#4A5C7A",
  default: "#6B7280",
};

export default function AdminSharedCalendar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [baseDate, setBaseDate] = useState(getMonday(new Date()));
  const [dayIdx, setDayIdx] = useState(() => {
    const dow = new Date().getDay();
    return dow === 0 ? 4 : Math.min(dow - 1, 4);
  });
  const [campusFilter, setCampusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const focusProviderId = searchParams.get("provider") || null;

  const dates = useMemo(
    () => [0, 1, 2, 3, 4].map((o) => isoOfDay(baseDate, o)),
    [baseDate]
  );

  // FP3-053 — Super-admin context narrowing.
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();

  // FP3-120 — Weekly team schedule + conflicts fetched as one
  // atomic bundle so a revisit to the same week is a single cache
  // hit. Week navigation opens a different cache slot.
  const teamParams = useMemo(
    () => ({
      start_date: dates[0],
      end_date: dates[4],
      ...(asOrg ? { as_org: asOrg } : {}),
      ...(asSchool ? { as_school: asSchool } : {}),
    }),
    [dates, asOrg, asSchool]
  );
  const teamQuery = useScheduleTeamWeek(teamParams);
  const providers = teamQuery.data?.providers || [];
  const sessions = teamQuery.data?.sessions || [];
  const conflicts = teamQuery.data?.conflicts || [];

  // Provider unavailability — separate slower-moving cache key.
  const providerListQuery = useAdminProviders(
    asOrg || asSchool
      ? {
          ...(asOrg ? { as_org: asOrg } : {}),
          ...(asSchool ? { as_school: asSchool } : {}),
        }
      : undefined,
  );
  const unavailQuery = useQuery({
    queryKey: [
      "provider-unavailability",
      authScopeKey(auth),
      (providerListQuery.data || []).map((p) => p.id).sort(),
    ],
    // Only fetch once the provider list resolves; otherwise cache
    // would key on an empty list and never re-key when providers
    // arrive on the second render.
    enabled: (providerListQuery.data || []).length > 0,
    queryFn: async () => {
      const list = providerListQuery.data || [];
      const all = await Promise.all(
        list.map(async (p) => {
          try {
            const r = await api.listProviderUnavailability({ provider_id: p.id });
            return (r.data || []).map((u) => ({ ...u, provider_id: p.id }));
          } catch {
            return [];
          }
        })
      );
      return all.flat();
    },
    staleTime: STALE.CALENDAR,
  });
  const unavail = unavailQuery.data || [];
  const loading =
    teamQuery.isPending || (providerListQuery.data && unavailQuery.isPending);
  const refetching =
    (teamQuery.isFetching && !teamQuery.isPending) ||
    (unavailQuery.isFetching && !unavailQuery.isPending);
  const dataUpdatedAt = Math.max(
    teamQuery.dataUpdatedAt || 0,
    unavailQuery.dataUpdatedAt || 0,
  );

  // FP3-120 — Group duplicate-slot rows (same date+start+end+provider)
  // into ONE occurrence carrying all live students. Mirrors
  // ScheduleView so Admin and Provider calendars stay in sync.
  const groupedSessions = useMemo(
    () => groupSessionsByTimeSlot(sessions),
    [sessions],
  );

  const campuses = useMemo(() => ["all", ...new Set(providers.map((p) => p.campus).filter(Boolean))], [providers]);
  const roles = useMemo(() => ["all", ...new Set(providers.map((p) => p.role).filter(Boolean))], [providers]);

  const visibleProviders = useMemo(() => {
    return providers.filter((p) => {
      if (focusProviderId) return p.id === focusProviderId;
      if (campusFilter !== "all" && p.campus !== campusFilter) return false;
      if (roleFilter !== "all" && p.role !== roleFilter) return false;
      return true;
    });
  }, [providers, campusFilter, roleFilter, focusProviderId]);

  const conflictSet = useMemo(() => {
    const s = new Set();
    conflicts.forEach((c) => (c.session_ids || []).forEach((id) => s.add(id)));
    return s;
  }, [conflicts]);
  // A grouped occurrence is "in conflict" if ANY of the underlying
  // session_ids are flagged. Falls back to `s.id` for one-off rows
  // that didn't get merged.
  const isGroupInConflict = (s) => {
    if (Array.isArray(s.session_ids)) {
      return s.session_ids.some((id) => conflictSet.has(id));
    }
    return conflictSet.has(s.id);
  };

  const dayDate = dates[dayIdx];
  // FP3-120 — Use the grouped list so multi-student blocks/slots
  // render as ONE occurrence with all students listed.
  const todaySessions = useMemo(
    () => groupedSessions.filter((s) => s.date === dayDate),
    [groupedSessions, dayDate],
  );
  const dayHours = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
  const startHour = dayHours[0];
  const endHour = dayHours[dayHours.length - 1] + 1;

  const navWeek = (dir) => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + dir * 7);
    setBaseDate(d);
  };

  const isUnavailToday = (providerId, iso) =>
    unavail.some((u) => u.provider_id === providerId && u.start_date <= iso && u.end_date >= iso);

  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("provider");
    setSearchParams(next, { replace: true });
  };

  return (
    <AdminShell
      title="Shared Calendar"
      subtitle="Cross-provider read-only view · click into any provider's full calendar via Providers"
      actions={
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev"
            onClick={() => navWeek(-1)}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
          >
            <CaretLeft size={14} />
          </button>
          <button
            data-testid="cal-today"
            onClick={() => {
              setBaseDate(getMonday(new Date()));
              const dow = new Date().getDay();
              setDayIdx(dow === 0 ? 4 : Math.min(dow - 1, 4));
            }}
            className="px-3 py-1 text-[11px] font-medium text-[#3F4756] bg-[#3F4756]/10 rounded-lg hover:bg-[#3F4756]/15"
          >
            Today
          </button>
          <button
            data-testid="cal-next"
            onClick={() => navWeek(1)}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
          >
            <CaretRight size={14} />
          </button>
          <span className="text-[12px] font-medium text-[#1F2937] ml-1">
            Week of {new Date(dates[0] + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
          <RefreshChip
            dataUpdatedAt={dataUpdatedAt || null}
            isFetching={refetching}
            onRefresh={() => {
              teamQuery.refetch();
              unavailQuery.refetch();
            }}
            testid="cal-refresh"
          />
        </div>
      }
    >
      {/* Filters */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Funnel size={14} className="text-[#6B7280]" />
          <span className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Campus</span>
          <div className="flex bg-[#F3F1EC] rounded-lg p-0.5">
            {campuses.map((c) => (
              <button
                key={c}
                data-testid={`cal-campus-${c}`}
                onClick={() => setCampusFilter(c)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
                  campusFilter === c ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
                }`}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Service area</span>
          <div className="flex bg-[#F3F1EC] rounded-lg p-0.5">
            {roles.map((r) => (
              <button
                key={r}
                data-testid={`cal-role-${r}`}
                onClick={() => setRoleFilter(r)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
                  roleFilter === r ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
                }`}
              >
                {r === "all" ? "All" : r}
              </button>
            ))}
          </div>
        </div>
        {focusProviderId && (
          <button
            data-testid="cal-clear-focus"
            onClick={clearFocus}
            className="text-[11px] text-[#3F4756] underline"
          >
            Clear single-provider focus
          </button>
        )}
        {conflicts.length > 0 && (
          <Badge className="bg-[#FBEFEC] text-[#B85C5C] hover:bg-[#FBEFEC] text-[10px] ml-auto">
            <Warning size={10} weight="fill" className="mr-1" /> {conflicts.length} cross-provider conflict{conflicts.length === 1 ? "" : "s"}
          </Badge>
        )}
      </div>

      {/* Day picker */}
      <div className="mb-4 flex items-center gap-1">
        {DAY_LABELS.map((lbl, idx) => {
          const iso = dates[idx];
          const isToday = iso === isoOfDay(new Date());
          return (
            <button
              key={lbl}
              data-testid={`cal-day-${lbl}`}
              onClick={() => setDayIdx(idx)}
              className={`flex-1 px-3 py-2 rounded-lg border text-[12px] font-medium ${
                dayIdx === idx
                  ? "bg-[#3F4756] text-white border-[#3F4756]"
                  : "bg-white text-[#1F2937] border-[#E5E3DB] hover:bg-[#FAFAF7]"
              }`}
            >
              <span className="block">{lbl}</span>
              <span className={`text-[10px] ${dayIdx === idx ? "text-white/80" : "text-[#9B9890]"}`}>
                {new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                {isToday && " · today"}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <Skeleton className="h-[480px]" />
      ) : visibleProviders.length === 0 ? (
        <p className="text-[13px] text-[#6B7280] py-12 text-center">No providers match the current filters.</p>
      ) : (
        <div className="border border-[#E5E3DB] rounded-lg overflow-x-auto bg-white" data-testid="shared-cal-grid">
          <div className="flex">
            {/* Time gutter */}
            <div className="shrink-0 w-12 border-r border-[#E5E3DB] bg-[#FAFAF7]">
              <div className="h-12 border-b border-[#E5E3DB]" />
              {dayHours.map((h) => (
                <div
                  key={h}
                  style={{ height: HOUR_PX }}
                  className="text-[10px] text-[#9B9890] text-right pr-1.5 pt-0.5 border-b border-[#ECEAE4]"
                >
                  {fmtHour(h)}
                </div>
              ))}
            </div>

            {/* Provider columns */}
            <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${visibleProviders.length}, minmax(140px, 1fr))` }}>
              {visibleProviders.map((p) => {
                const psessions = todaySessions.filter((s) => s.provider_id === p.id);
                const out = isUnavailToday(p.id, dayDate);
                return (
                  <div key={p.id} className="border-r border-[#E5E3DB] last:border-r-0 relative" data-testid={`cal-col-${p.id}`}>
                    {/* Header */}
                    <div className={`h-12 border-b border-[#E5E3DB] px-2.5 py-1.5 flex items-center justify-between gap-1 ${out ? "bg-[#FDF8F3]" : "bg-[#FAFAF7]"}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <span
                            className="inline-block w-2 h-2 rounded-full shrink-0"
                            style={{ background: ROLE_COLORS[p.role] || ROLE_COLORS.default }}
                          />
                          <p className="text-[12px] font-semibold text-[#1F2937] truncate">{p.first_name} {p.last_name}</p>
                        </div>
                        <p className="text-[9px] text-[#9B9890] truncate">{p.role}</p>
                      </div>
                      <button
                        onClick={() => navigate(`/admin/calendar?provider=${p.id}`)}
                        className="shrink-0 text-[#3F4756] hover:bg-white rounded p-1"
                        title="Focus on this provider"
                      >
                        <Eye size={11} />
                      </button>
                    </div>

                    {/* Out-of-office ribbon — subtle banner just under the
                        header so any real saved sessions for the day stay
                        visible underneath. */}
                    {out && (
                      <div
                        data-testid={`cal-unavail-${p.id}`}
                        className="bg-[#FDF8F3] border-b border-[#9C6B2E]/30 text-[#9C6B2E] text-[10px] font-medium px-2 py-1 text-center"
                      >
                        Unavailable today
                      </div>
                    )}

                    {/* Time grid */}
                    <div className="relative" style={{ height: HOUR_PX * (endHour - startHour) }}>
                      {dayHours.map((h, i) => (
                        <div
                          key={h}
                          className="absolute inset-x-0 border-b border-[#ECEAE4]"
                          style={{ top: i * HOUR_PX, height: HOUR_PX }}
                        />
                      ))}
                      {psessions.map((s) => {
                        const sm = timeToMinutes(s.start_time);
                        const em = timeToMinutes(s.end_time);
                        // FP3-035F Issue 1 — Clamp session pills to the
                        // visible 7AM-5PM grid. Legacy recurring blocks
                        // can land in MongoDB with start_time="00:00"
                        // end_time="23:59" (an "all-day" template
                        // marker) which used to render as a giant pink
                        // rectangle covering the entire column —
                        // making Sarah's column look distorted /
                        // pink-filled in the screenshot the user
                        // shared. We skip any pill that fully spans
                        // the visible window (≥10h) or that lands
                        // entirely outside the visible range.
                        const visibleStartMin = startHour * 60;
                        const visibleEndMin = endHour * 60;
                        const isAllDay = sm <= visibleStartMin && em >= visibleEndMin;
                        const isOutsideWindow =
                          em <= visibleStartMin || sm >= visibleEndMin;
                        if (isAllDay || isOutsideWindow) return null;
                        const clampedStart = Math.max(sm, visibleStartMin);
                        const clampedEnd = Math.min(em, visibleEndMin);
                        const top = ((clampedStart - visibleStartMin) / 60) * HOUR_PX;
                        const height = Math.max(
                          20,
                          ((clampedEnd - clampedStart) / 60) * HOUR_PX - 2,
                        );
                        const isConflict = isGroupInConflict(s);
                        const studentLabel = (s.students || []).map((st) => st.first_name).slice(0, 2).join(", ");
                        const accentBg = isConflict ? "bg-[#FBEFEC]" : "bg-[#E8F0EB]";
                        const accentBorder = isConflict ? "border-[#B85C5C]/40" : "border-[#1E5A3F]/30";
                        const accentText = isConflict ? "text-[#B85C5C]" : "text-[#1E5A3F]";
                        return (
                          <div
                            key={s.id}
                            data-testid={`cal-session-${s.id}`}
                            style={{ top, height, left: 4, right: 4 }}
                            className={`absolute rounded-md border ${accentBg} ${accentBorder} px-1.5 py-1 overflow-hidden`}
                            title={`${studentLabel || "Block"} · ${s.start_time}–${s.end_time}`}
                          >
                            <p className={`text-[10px] font-semibold ${accentText} truncate leading-tight`}>
                              {studentLabel || (s.block_kind === "personal" ? (s.personal_label || "Block") : "Session")}
                            </p>
                            <p className="text-[9px] text-[#6B7280] truncate leading-tight">
                              {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
